import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const WeeklyStorageReport = ({ storageData = [], customers = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'company', label: 'Company' },
        { key: 'storage_days', label: 'Storage Days' },
        { key: 'max_pallets_week', label: 'Max Pallets Week' },
        { key: 'total_pallets_handled', label: 'Total Pallets Handled' },
        { key: 'total_pallets_wrapped', label: 'Total Pallets Wrapped' },
        { key: 'total_pallets_banded', label: 'Total Pallets Banded' },
        { key: 'total_twenty_ft', label: 'Total 20ft' },
        { key: 'total_forty_ft', label: 'Total 40ft' },
        { key: 'total_cost', label: 'Total Cost' },
    ];

    // Helper function to get company name from customer_id
    const getCompanyName = (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        return customer ? customer.company : `Customer ${customerId}`;
    };

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                storageData.length = 0;
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error('Password validation error:', error);
            showAlert({
                message: 'An error occurred during authentication.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
    };

    const showData = () => {
        logger.log("Weekly Storage Data:", storageData);
        if (storageData && storageData.length > 0) {
            // Process data to add company names
            const processedData = storageData.map(item => ({
                ...item,
                company: getCompanyName(item.customer_id)
            }));
            setTableData(processedData);
            calculateSummaryStats(processedData);
        } else {
            setTableData([]);
            setSummaryStats({});
        }
        setOpenRequestPassword(false);
    };

    const calculateSummaryStats = (data) => {
        if (!data || data.length === 0) {
            setSummaryStats({});
            return;
        }

        const stats = {
            totalRecords: data.length,
            totalStorageDays: data.reduce((sum, item) => sum + (parseInt(item.storage_days) || 0), 0),
            totalPalletsHandled: data.reduce((sum, item) => sum + (parseFloat(item.total_pallets_handled) || 0), 0),
            totalPalletsWrapped: data.reduce((sum, item) => sum + (parseFloat(item.total_pallets_wrapped) || 0), 0),
            totalPalletsBanded: data.reduce((sum, item) => sum + (parseFloat(item.total_pallets_banded) || 0), 0),
            totalTwentyFt: data.reduce((sum, item) => sum + (parseFloat(item.total_twenty_ft) || 0), 0),
            totalFortyFt: data.reduce((sum, item) => sum + (parseFloat(item.total_forty_ft) || 0), 0),
            totalCost: data.reduce((sum, item) => sum + (parseFloat(item.total_cost) || 0), 0),
            uniqueCustomers: [...new Set(data.map(item => item.customer_id))].length,
            uniqueWeeks: [...new Set(data.map(item => item.week))].length,
        };

        setSummaryStats(stats);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '£0.00';
        return `£${parseFloat(value).toFixed(2)}`;
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        return parseFloat(value).toFixed(0);
    };

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {tableData && tableData.length > 0 ? (
                <div>
                    {/* Summary Stats */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Weekly Storage Summary</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <strong>Total Records:</strong> {summaryStats.totalRecords}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Unique Customers:</strong> {summaryStats.uniqueCustomers}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Unique Weeks:</strong> {summaryStats.uniqueWeeks}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Cost:</strong> {formatCurrency(summaryStats.totalCost)}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-3">
                                            <strong>Total Storage Days:</strong> {formatNumber(summaryStats.totalStorageDays)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Pallets Handled:</strong> {formatNumber(summaryStats.totalPalletsHandled)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Pallets Wrapped:</strong> {formatNumber(summaryStats.totalPalletsWrapped)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Pallets Banded:</strong> {formatNumber(summaryStats.totalPalletsBanded)}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-6">
                                            <strong>Total 20ft Containers:</strong> {formatNumber(summaryStats.totalTwentyFt)}
                                        </div>
                                        <div className="col-md-6">
                                            <strong>Total 40ft Containers:</strong> {formatNumber(summaryStats.totalFortyFt)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Weekly Storage Data ({tableData.length} records)</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'} p-0`}>
                                    <div className="table-responsive">
                                        <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                            <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                                                <tr>
                                                    {displayColumns.map((column) => (
                                                        <th key={column.key} className="text-center">
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.map((row, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{row.date || 'N/A'}</td>
                                                        <td className="text-center">{row.company || 'N/A'}</td>
                                                        <td className="text-center">{formatNumber(row.storage_days)}</td>
                                                        <td className="text-center">{formatNumber(row.max_pallets_week)}</td>
                                                        <td className="text-center">{formatNumber(row.total_pallets_handled)}</td>
                                                        <td className="text-center">{formatNumber(row.total_pallets_wrapped)}</td>
                                                        <td className="text-center">{formatNumber(row.total_pallets_banded)}</td>
                                                        <td className="text-center">{formatNumber(row.total_twenty_ft)}</td>
                                                        <td className="text-center">{formatNumber(row.total_forty_ft)}</td>
                                                        <td className="text-center">{formatCurrency(row.total_cost)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>No Weekly Storage Data Available</h4>
                            <p>No data found for the selected week.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WeeklyStorageReport;
